"use client";

import { useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { createEvoClient, messagingFormCreateMessage } from "@jti/evo-client";
import type { Config } from "@jti/fe-common";
import type { WithdrawalFormValues } from "@jti/ui/components/WithdrawalFormModal";

export type WithdrawalMessagePayload = {
  product?: string;
  language: string;
  market?: string;
};

type WithdrawalMessageConfig = Pick<Config, "EVO_DOMAIN" | "X-RECAPTCHA-ID" | "EVO_TENANT">;

const sendWithdrawalFormMessage = async (
  data: WithdrawalFormValues,
  messagePayload: WithdrawalMessagePayload,
  config: WithdrawalMessageConfig
): Promise<void> => {
  const client = createEvoClient({
    baseUrl: config.EVO_DOMAIN,
    recaptchaId: config["X-RECAPTCHA-ID"],
    tenantToken: config.EVO_TENANT,
    ciamToken: "",
    throwOnError: true,
  });

  const optionalPayloadFields = {
    ...(messagePayload.product ? { product: messagePayload.product } : {}),
    ...(messagePayload.market ? { market: messagePayload.market } : {}),
  };

  await messagingFormCreateMessage({
    client,
    body: {
      type: "CONTACT",
      user: {
        name: `${data.firstName} ${data.lastName}`,
        channel: data.email,
      },
      data_subject: data.email,
      payload: {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        language: messagePayload.language,
        ...optionalPayloadFields,
        topic: "Returns & warranty",
        subject: "[WITHDRAWAL]",
        query: `Order ID: #${data.selectedOrderValue}`,
      },
    },
  });
};

export const useWithdrawalFormMessage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const config = useConfig();

  const mutateAsync = async (data: WithdrawalFormValues, messagePayload: WithdrawalMessagePayload): Promise<void> => {
    setIsLoading(true);
    try {
      await sendWithdrawalFormMessage(data, messagePayload, config);
    } finally {
      setIsLoading(false);
    }
  };

  return { mutateAsync, isLoading };
};
